import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Button,
  DialogActions,
  CircularProgress,
  Box,
  Autocomplete,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import JobModel from "models/jobModel";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { Edit } from "@mui/icons-material";
import DepartmentModel from "models/departmentModel";
import DepartmentDialog from "components/AddDepartmentDialog";
import JobGroupDialog from "components/AddJobGroupDialog";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";
import { addJobGroupsBulk } from "services/api";

const AddJobDialog = ({
  open,
  onClose,
  job,
  departments,
  jobs,
  handleSubmit,
  handleDelete,
  handleOpenDeleteDialog,
  handleAddDepartment, // Function to add a department
  handleDeleteDepartment,
  handleEditDepartment,
  dep,
  jobToBranch,
  branches,
  jobGroups,
  handleAddJobGroup,
  handleDeleteJobGroup,
  refreshJobGroups,
}) => {
  const { t } = useTranslation();
  const MAX_VISIBLE_BRANCHES = 5;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [jobName, setJobName] = useState(job?.jobName || "");
  const [department, setDepartment] = useState(dep || null);
  const [directManager, setDirectManager] = useState(job?.reportingToSolidId || "");
  const [indirectManager, setIndirectManager] = useState(job?.reportingToDottedId || "");
  const [errors, setErrors] = useState({});
  const [seleectedBranches, setSelectedBranches] = useState([]);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // New state for adding a department
  const [newDepartmentDialogOpen, setNewDepartmentDialogOpen] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [editingDepartment, setEditingDepartment] = useState(false);
  const [depError, setDepError] = useState("");

  //New state for adding job group
  const [jobGroupDialogOpen, setJobGroupDialogOpen] = useState(false);
  const [newJobGroupName, setNewJobGroupName] = useState("");
  const [jobGroupError, setJobGroupError] = useState("");
  const [newJobGroups, setNewJobGroups] = useState([]);
  const [groupInput, setGroupInput] = useState("");

  const [contractualHours, setContractualHours] = useState(job?.contractualHours || ""); // New state for contractulal Hours

  useEffect(() => {
    if (job) {
      setJobName(job.jobName || "");
      setDepartment(dep || null);
      setDirectManager(job.reportingToSolidId || "");
      setIndirectManager(job.reportingToDottedId || "");
      // The job/branch links may not have loaded (or their request failed): start with none rather than crash.
      const assignedJobs = (jobToBranch || []).filter((item) => item.jobId === job.jobId).map((item) => item.branchId);
      setSelectedBranches(assignedJobs);
      setContractualHours(job.contractualHours || "");
    } else {
      setSelectedBranches([]); // Clear selection if adding new branch
    }
  }, [job, dep, departments, jobGroups]);

  const validate = () => {
    let newErrors = {};
    if (!department) newErrors.department = t("admin.jobs.jobDialog.departmentRequired");
    if (!jobName.trim()) {
      newErrors.jobName = t("admin.jobs.jobDialog.jobNameRequired");
    } else if (
      jobs.some((j) => j.jobName.toLowerCase() === jobName.toLowerCase() && j.jobId !== job?.jobId)
    ) {
      newErrors.jobName = t("admin.jobs.jobDialog.jobNameExists");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    const jobModel = new JobModel({
      jobId: job?.jobId,
      jobName,
      departmentId: department?.departmentId || null,
      departmentName: department?.departmentName || "",
      reportingToSolidId: directManager || null,
      reportingToSolidLine: directManager
        ? jobs.find((e) => e.jobId === directManager).jobName
        : null,
      reportingToDottedId: indirectManager || null,
      reportingToDottedLine: indirectManager
        ? jobs.find((e) => e.jobId === indirectManager).jobName
        : null,
      contractualHours: contractualHours !== "" ? parseInt(contractualHours, 10) : null,
    });
    // await handleSubmit(jobModel, seleectedBranches);
    const savedJob = await handleSubmit(jobModel, seleectedBranches);
    // 2️⃣ Save job groups (if any)
    if (newJobGroups.length > 0 && savedJob?.jobId) {
      const result = await addJobGroupsBulk(
        savedJob.jobId,
        newJobGroups // jobGroups = array of strings from your input
      );

      if (result !== "error") {
        await refreshJobGroups(); // 🔥 now parent state updates
      }

      if (result === "error") {
        console.error("Failed to save job groups");
      }
    }
    handleClose();
    setLoading(false);
  };

  const handleClose = () => {
    setJobName("");
    setDepartment(null);
    setDirectManager("");
    setIndirectManager("");
    setErrors({});
    setNewJobGroups([]);
    setGroupInput("");
    setContractualHours("");
    onClose();
  };

  const handleAddNewDepartment = async () => {
    if (!newDepartmentName.trim()) return;
    // Check if department name already exists (case-insensitive)
    const isDuplicate = departments.some(
      (dep) => dep.departmentName.toLowerCase().trim() === newDepartmentName.toLowerCase().trim()
    );

    if (isDuplicate) {
      setDepError(t("admin.jobs.jobDialog.departmentExists"));
      return;
    }

    await handleAddDepartment(newDepartmentName);
    setNewDepartmentName("");
    setDepError("");
    setNewDepartmentDialogOpen(false);
  };

  const handleEditDep = async () => {
    if (!department || !department.departmentId) return;
    // Check if department name already exists (case-insensitive)
    const isDuplicate = departments.some(
      (dep) => dep.departmentName.toLowerCase().trim() === newDepartmentName.toLowerCase().trim()
    );

    if (isDuplicate) {
      setDepError(t("admin.jobs.jobDialog.departmentExists"));
      return;
    }
    const newDep = new DepartmentModel({
      ...department,
      departmentName: newDepartmentName,
    });
    await handleEditDepartment(newDep);
    setNewDepartmentName("");
    setDepError("");
    setNewDepartmentDialogOpen(false);
  };

  const handleDepDelete = async () => {
    if (!department || !department.departmentId) return;
    await handleDeleteDepartment(department.departmentId);
    setNewDepartmentName("");
    setDepError("");

    setNewDepartmentDialogOpen(false);
  };

  const handleAddNewJobGroup = async () => {
    if (!newJobGroupName.trim()) {
      setJobGroupError(t("admin.jobs.jobDialog.jobGroupEmpty"));
      return;
    }
    const newJobGroup = {
      jobGroup: newJobGroupName,
      jobId: job?.jobId,
    };
    // Check if job group name already exists (case-insensitive)
    const isDuplicate = jobGroups.some(
      (group) => group.jobGroup.toLowerCase().trim() === newJobGroupName.toLowerCase().trim()
    );

    if (isDuplicate) {
      setJobGroupError(t("admin.jobs.jobDialog.jobGroupExists"));
      return;
    }
    await handleAddJobGroup(newJobGroup);
    setNewJobGroupName("");
    setJobGroupError("");
    setJobGroupDialogOpen(false);
  };

  const handleJobGroupDelete = async () => {
    setNewJobGroupName("");
    setJobGroupError("");
    setJobGroupDialogOpen(false);
  };

  const handleOpenDetails = () => {
    navigate("/job-details", {
      state: {
        jobs,
        jobId: job?.jobId,
        departments,
        jobGroups,
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          backgroundColor: darkMode ? "#333" : "#fff",
          color: darkMode ? "#fff" : "#000",
          borderRadius: 2,
          padding: 2,
        },
      }}
      fullWidth
    >
      <DialogTitle>
        {job ? t("admin.jobs.jobDialog.editJob") : t("admin.jobs.jobDialog.addJob")}
        {job && (
          <Button
            sx={{ float: "right", marginTop: "-8px" }}
            color="primary"
            onClick={handleOpenDetails}
          >
            {t("admin.jobs.jobDialog.jobDetails")}
          </Button>
        )}
      </DialogTitle>
      <DialogContent>
        {/* Department Selection */}
        <Box display="flex" alignItems="center" sx={{ width: "100%", gap: 1 }}>
          <Autocomplete
            value={department}
            onChange={(event, newValue) => {
              setDepartment(newValue);
              if (errors.department) setErrors({ ...errors, department: null });
            }}
            options={departments}
            getOptionLabel={(option) => option.departmentName || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.jobs.jobDialog.department") + " *"}
                fullWidth
                margin="normal"
                error={!!errors.department}
                helperText={errors.department}
              />
            )}
            sx={{ flex: 1 }}
          />

          {/* Add Button - Always Visible */}
          <IconButton
            onClick={() => {
              setNewDepartmentName("");
              setDepError("");
              setEditingDepartment(null);
              setNewDepartmentDialogOpen(true);
            }}
            color="primary"
          >
            <AddIcon />
          </IconButton>

          {/* Edit Button - Only visible when a department is selected */}
          {department && (
            <IconButton
              onClick={() => {
                setNewDepartmentName(department.departmentName);
                setEditingDepartment(department);
                setNewDepartmentDialogOpen(true);
              }}
              color="primary"
            >
              <Edit />
            </IconButton>
          )}
        </Box>

        {/* Job Name */}
        <TextField
          label={t("admin.jobs.jobDialog.jobName") + " *"}
          value={jobName}
          onChange={(e) => {
            setJobName(e.target.value);
            if (errors.jobName) setErrors({ ...errors, jobName: null });
          }}
          fullWidth
          margin="normal"
          error={!!errors.jobName}
          helperText={errors.jobName}
        />

        {!job && (
          <>
            <TextField
              label={t("admin.jobs.jobDialog.jobGroups")}
              value={groupInput}
              onChange={(e) => setGroupInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && groupInput.trim()) {
                  e.preventDefault();

                  // prevent duplicates
                  if (!newJobGroups?.includes(groupInput.trim())) {
                    setNewJobGroups((prev) => [...prev, groupInput.trim()]);
                  }

                  setGroupInput("");
                }
              }}
              fullWidth
              margin="normal"
              helperText={t("admin.jobs.jobDialog.jobHelper")}
            />

            <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {newJobGroups.map((group, index) => (
                <Chip
                  key={index}
                  label={group}
                  onDelete={() => setNewJobGroups((prev) => prev?.filter((_, i) => i !== index))}
                />
              ))}
            </Box>
          </>
        )}

        {/* Job Group Selection */}
        {job && (
          <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
            <Autocomplete
              multiple
              disableClearable
              options={jobGroups} // All available jobGroups
              getOptionLabel={(group) => group.jobGroup || ""}
              value={jobGroups?.filter((group) => group.jobId === job?.jobId)}
              onChange={async (event, newValue, reason, details) => {
                if (reason === "removeOption" && details?.option) {
                  const deletedGroup = details.option;
                  try {
                    await handleDeleteJobGroup(deletedGroup.jobGroupId);
                  } catch (error) {}
                }
              }}
              renderTags={(selected, getTagProps) => {
                const MAX_VISIBLE_TAGS = 6;
                const visibleGroups = selected.slice(0, MAX_VISIBLE_TAGS);
                const hiddenGroups = selected.length - MAX_VISIBLE_TAGS;

                return (
                  <>
                    {visibleGroups.map((group, index) => (
                      <Chip
                        key={group.jobGroupId}
                        label={group.jobGroup}
                        {...getTagProps({ index })}
                      />
                    ))}
                    {hiddenGroups > 0 && (
                      <Tooltip title={selected.map((g) => g.jobGroup).join(", ")}>
                        <Chip label={`+${hiddenGroups} `} sx={{ cursor: "pointer" }} />
                      </Tooltip>
                    )}
                  </>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("admin.jobs.jobDialog.jobGroups")}
                  fullWidth
                  margin="normal"
                />
              )}
              sx={{ flex: 1 }}
            />

            {/* IconButton Adjusts Based on Selection */}
            <IconButton
              onClick={() => {
                setJobGroupError("");
                setJobGroupDialogOpen(true);
              }}
              color="primary"
            >
              <AddIcon />
            </IconButton>
          </Box>
        )}

        {/* Multi-Select Jobs Dropdown */}
        <Autocomplete
          multiple
          options={branches}
          getOptionLabel={(branch) => branch.branchName}
          value={branches?.filter((branch) => seleectedBranches?.includes(branch.branchId))} // Match selected branches
          onChange={(event, newValue) => {
            setSelectedBranches(newValue.map((branch) => branch.branchId));
          }}
          renderTags={(selected, getTagProps) => {
            if (!branches) return;
            const visibleBranches = selected.slice(0, MAX_VISIBLE_BRANCHES);
            const hiddenBranches = selected.length - MAX_VISIBLE_BRANCHES;

            return (
              <>
                {visibleBranches.map((branch, index) => (
                  <Chip
                    key={branch.branchId}
                    label={branch.branchName}
                    {...getTagProps({ index })}
                  />
                ))}
                {hiddenBranches > 0 && (
                  <Tooltip title={selected.map((branch) => branch.branchName).join(", ")}>
                    <Chip label={`+${hiddenBranches} `} sx={{ cursor: "pointer" }} />
                  </Tooltip>
                )}
              </>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.jobs.jobDialog.selectBranches")}
              margin="normal"
              fullWidth
            />
          )}
        />

        <Autocomplete
          options={jobs?.filter((j) => !job || job.jobId !== j.jobId)} // Exclude the current job if editing
          getOptionLabel={(option) => option.jobName} // Display job name
          value={jobs?.find((j) => j.jobId === directManager) || null} // Find the selected manager
          onChange={(event, newValue) => {
            setDirectManager(newValue ? newValue.jobId : "");
            setIndirectManager(newValue?.reportingToSolidId ?? "");
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.jobs.jobDialog.reportingToSolid")}
              margin="normal"
              fullWidth
            />
          )}
        />

        <Autocomplete
          // Options: Filter out the current job (self) to prevent self-reporting
          options={jobs?.filter((j) => !job || job.jobId !== j.jobId)}
          // What text to display in the list
          getOptionLabel={(option) => option.jobName || ""}
          // Bind the selected ID (indirectManager) back to the full object for the component
          value={jobs?.find((j) => j.jobId === indirectManager) || null}
          // Update state: Extract ID if a value is selected, otherwise empty string
          onChange={(event, newValue) => {
            setIndirectManager(newValue ? newValue.jobId : "");
          }}
          // If you want to keep it disabled like your previous code, add: disabled={true}
          // If you want it enabled like the one above, remove the disabled prop entirely.

          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.jobs.jobDialog.reportingToDotted")}
              margin="normal"
              fullWidth
              sx={fieldStyles} // Preserving your existing styles
            />
          )}
        />

        <TextField
          type="number"
          fullWidth
          label={t("user.hoursCalculator.contractualHrs")}
          value={contractualHours}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "" || Number(value) > 0) {
              setContractualHours(value);
            }
          }}
          // Ensure the browser's number input doesn't go below 0
          inputProps={{ min: 0 }}
          variant="outlined"
          sx={{
            mt: 2,
            ...fieldStyles,
          }}
        />
      </DialogContent>

      <DepartmentDialog
        open={newDepartmentDialogOpen}
        onClose={() => {
          setNewDepartmentDialogOpen(false);
          setNewDepartmentName("");
          setDepError("");
        }}
        onSave={editingDepartment ? handleEditDep : handleAddNewDepartment}
        onDelete={handleDepDelete}
        departmentName={newDepartmentName}
        setDepartmentName={setNewDepartmentName}
        error={depError}
        isEditing={editingDepartment}
      />

      <JobGroupDialog
        open={jobGroupDialogOpen}
        onClose={() => {
          setJobGroupDialogOpen(false);
          setNewJobGroupName("");
          setJobGroupError("");
        }}
        onSave={handleAddNewJobGroup}
        onDelete={handleJobGroupDelete}
        jobGroupName={newJobGroupName}
        setJobGroupName={setNewJobGroupName}
        error={jobGroupError}
      />

      {loading ? (
        <Box display="flex" justifyContent="flex-end" p={1}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <DialogActions>
          <Button
            // onClick={onClose}
            onClick={() => {
              handleClose();
            }}
            color="secondary"
          >
            {t("admin.jobs.jobDialog.cancel")}
          </Button>
          {job && (
            <Button
              onClick={async () => {
                setLoading(true);
                // await handleDelete(job.jobId);
                handleOpenDeleteDialog(job?.jobId);
                setLoading(false);
                handleClose();
              }}
              color="error"
              sx={{
                color: "red",
                "&:hover": { backgroundColor: "red", color: "#fff" },
              }}
            >
              {t("admin.jobs.jobDialog.delete")}
            </Button>
          )}
          <Button
            onClick={handleSave}
            color="primary"
            sx={{
              "&:hover": { backgroundColor: "blue", color: "#fff" },
            }}
          >
            {job ? t("admin.jobs.jobDialog.update") : t("admin.jobs.jobDialog.add")}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

AddJobDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  job: PropTypes.object,
  departments: PropTypes.array.isRequired,
  jobs: PropTypes.array.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleOpenDeleteDialog: PropTypes.func.isRequired,
  handleAddDepartment: PropTypes.func.isRequired, // New function to add department
  dep: PropTypes.object,
  jobToBranch: PropTypes.array.isRequired,
  branches: PropTypes.array.isRequired,
  handleDeleteDepartment: PropTypes.func.isRequired,
  handleEditDepartment: PropTypes.func.isRequired,
  jobGroups: PropTypes.array.isRequired,
  handleAddJobGroup: PropTypes.func.isRequired,
  handleDeleteJobGroup: PropTypes.func.isRequired,
  refreshJobGroups: PropTypes.func.isRequired,
};

export default AddJobDialog;
